import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { useReport } from '../reporting/ReportContext';
import { DateRangeBar } from '../reporting/DateRangeBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { SimpleBars } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { addDays, downloadCsv, monthLabel, num, shortDate, type DpdReport } from '../lib/reporting';

const DPD = CHANNEL_COLORS.DPD;

export function DpdHandoffs() {
  // DPD handoffs are date-filtered only — they carry no NVS region/driver — so
  // this page has its own date controls rather than the shared NVS filter bar.
  const { options } = useReport();
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [data, setData] = useState<DpdReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Default to the last 30 days of available data once bounds are known.
  useEffect(() => {
    if (options?.dataMax) {
      setRange((prev) => (prev.from || prev.to ? prev : { to: options.dataMax!, from: addDays(options.dataMax!, -29) }));
    }
  }, [options]);

  useEffect(() => {
    if (!range.from || !range.to) return;
    let active = true;
    const p = new URLSearchParams({ from: range.from, to: range.to });
    apiFetch<DpdReport>(`/api/reports/dpd?${p.toString()}`)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load DPD report');
      });
    return () => {
      active = false;
    };
  }, [range.from, range.to]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(
      `nvs-dpd-handoffs-daily-${range.from}_${range.to}.csv`,
      data.daily.map((d) => ({ date: d.date, dpd_stops: d.stops })),
    );
  };

  const perMonth = data && data.monthly.length ? Math.round(data.total / data.monthly.length) : 0;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / DPD handoffs"
        title="DPD handoffs"
        subtitle={
          data
            ? `${data.from} → ${data.to} · third-party courier handoffs · counted separately, excluded from all NVS figures`
            : 'Third-party courier handoffs'
        }
      />
      <DateRangeBar
        from={range.from}
        to={range.to}
        min={options?.dataMin ?? undefined}
        max={options?.dataMax ?? undefined}
        onChange={(next) => setRange((prev) => ({ ...prev, ...next }))}
        onExport={exportCsv}
      />

      {error && <p className="form-error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}

      {data && (
        <>
          <div className="callout">
            DPD stops are identified by their parcel barcode and are <strong>not</strong> included in any
            NVS stop count or DIFOT figure. NVS hands these off to DPD (mainly remote postcodes), so
            their delivery outcome is tracked by DPD, not here — only the handoff volume is shown.
          </div>

          <div className="kpi-grid">
            <KpiCard label="DPD handoffs" value={num(data.total)} sub="In selected range" accent={DPD} />
            <KpiCard label="Avg per month" value={num(perMonth)} sub={`${data.monthly.length} month(s)`} accent={DPD} />
            <KpiCard label="Weeks covered" value={num(data.weekly.length)} sub="With at least one handoff" accent={DPD} />
            <KpiCard label="Postcode areas" value={num(data.byArea.length)} sub="Distinct destination areas" accent={DPD} />
          </div>

          <div className="panel-grid">
            <Panel title="Monthly" subtitle="DPD handoffs per month">
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Month</th><th className="num">DPD stops</th></tr></thead>
                  <tbody>
                    {data.monthly.map((m) => (
                      <tr key={m.month}><td>{monthLabel(m.month)}</td><td className="num">{num(m.stops)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Weekly" subtitle="DPD handoffs per week (w/c)">
              <SimpleBars
                data={data.weekly.map((w) => ({ label: shortDate(w.week), value: w.stops }))}
                format={(v) => num(v)}
                color={DPD}
              />
            </Panel>
          </div>

          <Panel title="Daily" subtitle="DPD handoffs per day">
            {data.daily.length > 0 ? (
              <SimpleBars
                data={data.daily.map((d) => ({ label: shortDate(d.date), value: d.stops }))}
                format={(v) => num(v)}
                color={DPD}
              />
            ) : (
              <p className="muted">No DPD handoffs in this range.</p>
            )}
          </Panel>

          <Panel title="By destination postcode area" subtitle="Where NVS hands off to DPD">
            <div className="data-table-wrap">
              <table className="data-table">
                <thead><tr><th>Postcode area</th><th className="num">DPD stops</th></tr></thead>
                <tbody>
                  {data.byArea.map((a) => (
                    <tr key={a.area}><td>{a.area}</td><td className="num">{num(a.stops)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </section>
  );
}
